/**
 * Phone Number Formatting Utility
 *
 * Purpose: Format normalized Korean phone numbers for display
 *
 * Input: 01012345678 (digits only, normalized format from DB)
 * Output: 010-1234-5678 (formatted for user-friendly display)
 *
 * Ported from: PhoneNumberUtils.java
 * @author Claude Code
 * @since 2025-11-19
 */

/**
 * Format normalized phone number for display
 *
 * @param phoneNumber Normalized phone number (01012345678) or E.164 format (+821012345678)
 * @returns Formatted phone number (010-1234-5678) or original if invalid
 */
export function formatPhoneNumber(phoneNumber: string | null | undefined): string {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return '-';
  }

  let phone = phoneNumber.trim();

  // E.164 international format: +821012345678 → 01012345678
  if (phone.startsWith('+82')) {
    phone = '0' + phone.substring(3);
  }

  // Remove any remaining non-digit characters
  phone = phone.replace(/[^\d]/g, '');

  // Mobile number: 010-1234-5678
  if (/^01[016789]\d{7,8}$/.test(phone)) {
    if (phone.length === 10) {
      // 010-123-4567
      return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
    } else if (phone.length === 11) {
      // 010-1234-5678
      return `${phone.substring(0, 3)}-${phone.substring(3, 7)}-${phone.substring(7)}`;
    }
  }

  // Seoul landline: 02-1234-5678
  if (/^02\d{7,8}$/.test(phone)) {
    if (phone.length === 9) {
      // 02-123-4567
      return `${phone.substring(0, 2)}-${phone.substring(2, 5)}-${phone.substring(5)}`;
    } else if (phone.length === 10) {
      // 02-1234-5678
      return `${phone.substring(0, 2)}-${phone.substring(2, 6)}-${phone.substring(6)}`;
    }
  }

  // Other area codes: 031-123-4567
  if (/^0(3[1-3]|4[1-4]|5[1-5]|6[1-4])\d{7,8}$/.test(phone)) {
    if (phone.length === 10) {
      // 031-123-4567
      return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
    } else if (phone.length === 11) {
      // 031-1234-5678
      return `${phone.substring(0, 3)}-${phone.substring(3, 7)}-${phone.substring(7)}`;
    }
  }

  // Special numbers or invalid format - return as is
  return phone;
}

/**
 * Format phone number with fallback to original value
 * Useful when you want to show the original value if formatting fails
 *
 * @param phoneNumber Phone number to format
 * @param fallback Fallback value if phone is null/undefined (default: '-')
 * @returns Formatted phone number or fallback
 */
export function formatPhoneNumberSafe(
  phoneNumber: string | null | undefined,
  fallback: string = '-'
): string {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return fallback;
  }
  return formatPhoneNumber(phoneNumber);
}
